// src/components/Presentation/LivePresentation.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Play, 
  Pause, 
  ChevronLeft, 
  ChevronRight, 
  Users, 
  MessageCircle,
  BarChart3,
  Settings,
  Share,
  X,
  Maximize,
  Eye,
  EyeOff,
  Volume2,
  VolumeX,
  Presentation,
  QrCode
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getPresentation, 
  createLiveSession, 
  getSession, 
  updateSessionSlide, 
  endSession,
  subscribeToSession 
} from '../../services/presentationAPI';
import SlideRenderer from './SlideRenderer';
import AudiencePanel from './AudiencePanel';
import QRCodeGenerator from './QRCodeGenerator';
import toast from 'react-hot-toast';

const LivePresentation = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionCode = searchParams.get('session');
  
  const [presentation, setPresentation] = useState(null);
  const [session, setSession] = useState(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showAudiencePanel, setShowAudiencePanel] = useState(true);
  const [showSpeakerNotes, setShowSpeakerNotes] = useState(true);
  const [showQRCode, setShowQRCode] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  
  const presenterRef = useRef(null);
  const unsubscribeRef = useRef(null);

  useEffect(() => {
    loadPresentationAndSession();
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [id, sessionCode]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          previousSlide();
          break;
        case 'ArrowRight':
        case 'ArrowDown':
        case ' ':
          e.preventDefault();
          nextSlide();
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'Escape':
          if (isFullscreen) {
            toggleFullscreen();
          }
          break;
        case 'q':
        case 'Q':
          e.preventDefault();
          setShowQRCode(!showQRCode);
          break;
        case 'n':
        case 'N':
          e.preventDefault();
          setShowSpeakerNotes(!showSpeakerNotes);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlideIndex, presentation?.slides?.length, isFullscreen, showQRCode, showSpeakerNotes]);

  const loadPresentationAndSession = async () => {
    try {
      setIsLoading(true);
      
      // Load presentation
      const presentationData = await getPresentation(id);
      setPresentation(presentationData);

      if (sessionCode) {
        // Subscribe to session updates
        const sessionQuery = await subscribeToSession(sessionCode, (sessionDoc) => {
          if (sessionDoc.exists()) {
            const sessionData = { id: sessionDoc.id, ...sessionDoc.data() };
            setSession(sessionData);
            setParticipants(sessionData.participants || []);
            setInteractions(sessionData.interactions || []);
            setCurrentSlideIndex(sessionData.currentSlide || 0);
            setConnectionStatus('connected');
          } else {
            setConnectionStatus('disconnected');
          }
        });
        
        unsubscribeRef.current = sessionQuery;
      }
    } catch (error) {
      toast.error('Error loading live presentation');
      console.error(error);
      setConnectionStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  const nextSlide = async () => {
    if (currentSlideIndex < (presentation?.slides?.length - 1)) {
      const newIndex = currentSlideIndex + 1;
      setCurrentSlideIndex(newIndex);
      
      if (session) {
        await updateSessionSlide(session.id, newIndex);
      }
    }
  };

  const previousSlide = async () => {
    if (currentSlideIndex > 0) {
      const newIndex = currentSlideIndex - 1;
      setCurrentSlideIndex(newIndex);
      
      if (session) {
        await updateSessionSlide(session.id, newIndex);
      }
    }
  };

  const goToSlide = async (slideIndex) => {
    setCurrentSlideIndex(slideIndex);
    
    if (session) {
      await updateSessionSlide(session.id, slideIndex);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen && presenterRef.current) {
      if (presenterRef.current.requestFullscreen) {
        presenterRef.current.requestFullscreen();
      }
      setIsFullscreen(true);
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const shareSession = async () => {
    const shareUrl = `${window.location.origin}/join/${session?.sessionCode}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: presentation?.title,
          text: 'Join this live presentation',
          url: shareUrl
        });
      } catch (error) {
        navigator.clipboard.writeText(shareUrl);
        toast.success('Session link copied to clipboard');
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Session link copied to clipboard');
    }
  };

  const endSession = async () => {
    if (window.confirm('Are you sure you want to end this live session?')) {
      try {
        await updateSessionSlide(session.id, -1); // Signal end
        navigate(`/presentation/${id}/edit`);
        toast.success('Live session ended');
      } catch (error) {
        toast.error('Error ending session');
        console.error(error);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
          <p className="mt-4 text-white">Loading live presentation...</p>
        </div>
      </div>
    );
  }

  const currentSlide = presentation?.slides?.[currentSlideIndex];

  return (
    <div ref={presenterRef} className="min-h-screen bg-gray-900 text-white flex">
      {/* Main Presentation Area */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <div className="bg-black bg-opacity-50 backdrop-blur-sm p-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-lg font-semibold">{presentation?.title}</h1>
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${
                connectionStatus === 'connected' ? 'bg-green-500' :
                connectionStatus === 'connecting' ? 'bg-yellow-500' :
                'bg-red-500'
              }`}></div>
              <span className="text-sm text-gray-300">
                {connectionStatus === 'connected' ? 'Live' :
                 connectionStatus === 'connecting' ? 'Connecting...' :
                 'Disconnected'}
              </span>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Participant count */}
            <div className="flex items-center space-x-2 px-3 py-1 bg-white bg-opacity-20 rounded-lg">
              <Users className="w-4 h-4" />
              <span className="text-sm">{participants.length}</span>
            </div>
            
            {/* Session code */}
            {session && (
              <div className="flex items-center space-x-2 px-3 py-1 bg-white bg-opacity-20 rounded-lg">
                <span className="text-sm">Code: {session.sessionCode}</span>
                <button
                  onClick={() => setShowQRCode(!showQRCode)}
                  className="p-1 hover:bg-white hover:bg-opacity-20 rounded"
                >
                  <QrCode className="w-4 h-4" />
                </button>
              </div>
            )}
            
            {/* Controls */}
            <button
              onClick={() => setShowSpeakerNotes(!showSpeakerNotes)}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              title="Toggle Speaker Notes"
            >
              {showSpeakerNotes ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
            </button>
            
            <button
              onClick={() => setShowAudiencePanel(!showAudiencePanel)}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              title="Toggle Audience Panel"
            >
              <MessageCircle className="w-5 h-5" />
            </button>
            
            <button
              onClick={shareSession}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              title="Share Session"
            >
              <Share className="w-5 h-5" />
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              title="Fullscreen (F)"
            >
              <Maximize className="w-5 h-5" />
            </button>
            
            <button
              onClick={endSession}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
            >
              End Session
            </button>
          </div>
        </div>

        {/* Slide Display */}
        <div className="flex-1 flex items-center justify-center p-8 relative">
          {/* Navigation Arrows */}
          <button
            onClick={previousSlide}
            disabled={currentSlideIndex === 0}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 p-3 bg-black bg-opacity-50 hover:bg-opacity-75 rounded-full transition-colors disabled:opacity-25"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <button
            onClick={nextSlide}
            disabled={currentSlideIndex === (presentation?.slides?.length - 1)}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 p-3 bg-black bg-opacity-50 hover:bg-opacity-75 rounded-full transition-colors disabled:opacity-25"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Slide Content */}
          {currentSlide ? (
            <SlideRenderer
              slide={currentSlide}
              isLive={true}
              sessionId={session?.id}
              onInteraction={(interaction) => {
                // Handle live interactions
                console.log('Live interaction:', interaction);
              }}
            />
          ) : (
            <div className="text-center">
              <Presentation className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-400">No slides to display</p>
            </div>
          )}
        </div>

        {/* Bottom Controls */}
        <div className="bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm">
                Slide {currentSlideIndex + 1} of {presentation?.slides?.length || 0}
              </span>
              
              {/* Slide thumbnails */}
              <div className="flex space-x-2 overflow-x-auto max-w-lg">
                {presentation?.slides?.map((slide, index) => (
                  <button
                    key={slide.id}
                    onClick={() => goToSlide(index)}
                    className={`flex-shrink-0 w-16 h-12 border-2 rounded overflow-hidden transition-colors ${
                      currentSlideIndex === index
                        ? 'border-white'
                        : 'border-gray-500 hover:border-gray-300'
                    }`}
                  >
                    <div 
                      className="w-full h-full"
                      style={{ backgroundColor: slide.background?.value || '#ffffff' }}
                    >
                      {/* Mini slide preview */}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-400">
                Use ← → arrows or space to navigate
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Speaker Notes Panel */}
      {showSpeakerNotes && (
        <div className="w-80 bg-gray-800 border-l border-gray-700 flex flex-col">
          <div className="p-4 border-b border-gray-700">
            <h3 className="font-medium">Speaker Notes</h3>
          </div>
          <div className="flex-1 p-4 overflow-y-auto">
            {currentSlide?.notes ? (
              <p className="text-sm text-gray-300 whitespace-pre-wrap">
                {currentSlide.notes}
              </p>
            ) : (
              <p className="text-sm text-gray-500 italic">
                No speaker notes for this slide
              </p>
            )}
          </div>
          
          {/* Next slide preview */}
          {currentSlideIndex < (presentation?.slides?.length - 1) && (
            <div className="p-4 border-t border-gray-700">
              <h4 className="text-xs font-medium text-gray-400 mb-2">Next Slide</h4>
              <div className="w-full h-20 bg-gray-700 rounded border border-gray-600 flex items-center justify-center">
                <span className="text-xs text-gray-400">
                  {presentation.slides[currentSlideIndex + 1]?.title || `Slide ${currentSlideIndex + 2}`}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Audience Panel */}
      {showAudiencePanel && (
        <AudiencePanel
          session={session}
          participants={participants}
          interactions={interactions}
          onClose={() => setShowAudiencePanel(false)}
        />
      )}

      {/* QR Code Modal */}
      {showQRCode && session && (
        <QRCodeGenerator
          sessionCode={session.sessionCode}
          presentationTitle={presentation?.title}
          onClose={() => setShowQRCode(false)}
        />
      )}
    </div>
  );
};

export default LivePresentation;